import React, { useEffect, useState } from 'react';
import { supportFetch, fmtBytes, fmtDate, fmtNum } from '../api';
import ExportButton from '../components/ExportButton';

// Company list + drill-in detail (users, serials, files, recent events).
const CustomersTab = ({ token, onAuthFail }) => {
  const [companies, setCompanies] = useState([]);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);   // companyKey
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    supportFetch('/support/companies', token, onAuthFail)
      .then(d => setCompanies(d.companies || []))
      .catch(e => { if (e.message !== 'Session expired') setError('Failed to load companies.'); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (companyKey, companyId) => {
    setSelected(companyKey); setDetail(null); setDetailLoading(true);
    supportFetch(`/support/company/${encodeURIComponent(companyId)}`, token, onAuthFail)
      .then(setDetail)
      .catch(() => setDetail({ error: true }))
      .finally(() => setDetailLoading(false));
  };

  if (loading) return <div className="sp-loading">Loading companies…</div>;
  if (error)   return <div className="sp-error">{error}</div>;

  const filtered = companies.filter(c =>
    !search || `${c.display} ${c.companyId} ${c.companyKey}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="sp-customers">
      <div className="sp-toolbar">
        <input className="sp-search" placeholder="🔍 Search companies…" value={search} onChange={e => setSearch(e.target.value)} />
        <ExportButton rows={companies} name="companies" />
      </div>

      {filtered.length === 0 && <div className="sp-empty">No companies match.</div>}

      <table className="sp-table">
        <thead>
          <tr><th>Company</th><th>Files</th><th>Storage</th><th>Last upload</th><th></th></tr>
        </thead>
        <tbody>
          {filtered.map(c => (
            <tr key={c.companyKey} className={selected === c.companyKey ? 'sp-row-selected' : ''}>
              <td>{c.display}{c.unmapped && <span className="sp-tag">unmapped</span>}</td>
              <td>{fmtNum(c.files)}</td>
              <td>{fmtBytes(c.bytes)}</td>
              <td>{fmtDate(c.lastUpload)}</td>
              <td><button className="sp-btn sp-btn--sm" onClick={() => openDetail(c.companyKey, c.companyId)}>View</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {selected && (
        <div className="sp-panel sp-detail">
          {detailLoading && <div className="sp-loading">Loading detail…</div>}
          {detail && detail.error && <div className="sp-error">Failed to load company detail.</div>}
          {detail && !detail.error && (
            <>
              <div className="sp-detail-head">
                <h3>{detail.companyKey}</h3>
                <button className="sp-btn sp-btn--sm" onClick={() => setSelected(null)}>Close</button>
              </div>
              <div className="sp-stat-grid sp-stat-grid--sm">
                <div className="sp-stat-card"><div className="sp-stat-value">{fmtNum(detail.fileCount)}</div><div className="sp-stat-label">Files</div></div>
                <div className="sp-stat-card"><div className="sp-stat-value">{fmtBytes(detail.bytes)}</div><div className="sp-stat-label">Storage</div></div>
                <div className="sp-stat-card"><div className="sp-stat-value">{detail.users.length}</div><div className="sp-stat-label">Users</div></div>
                <div className="sp-stat-card"><div className="sp-stat-value">{detail.serials.length}</div><div className="sp-stat-label">Serials</div></div>
              </div>

              <h4>Users</h4>
              {detail.users.length === 0 ? <p className="sp-muted">No users.</p> : (
                <ul className="sp-list">{detail.users.map(u => <li key={u.username}>{u.username}{u.email ? ` — ${u.email}` : ''}</li>)}</ul>
              )}

              <h4>Serial numbers</h4>
              {detail.serials.length === 0 ? <p className="sp-muted">None.</p> : (
                <div className="sp-chips">{detail.serials.map(s => <span key={s} className="sp-chip">{s}</span>)}</div>
              )}

              <h4>Recent activity</h4>
              {detail.recentEvents.length === 0 ? <p className="sp-muted">No recent events.</p> : (
                <table className="sp-table sp-table--compact">
                  <thead><tr><th>Time</th><th>Type</th><th>Message</th></tr></thead>
                  <tbody>
                    {detail.recentEvents.map((e, i) => (
                      <tr key={i} className={`sp-lvl-${e.level}`}>
                        <td>{fmtDate(e.timestamp)}</td><td>{e.type}</td><td>{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomersTab;
